import React, { useCallback, useEffect, useRef, useState } from "react"
import { FileError, useDropzone } from "react-dropzone"
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import ErrorOutline from "@mui/icons-material/ErrorOutline"
import Toast from "./toast"

type Props = {
  files: File[]
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
  loading: boolean
  error: boolean
  fileLimit: number
  setError: React.Dispatch<React.SetStateAction<boolean>>
}

const AttachmentsDropzone: React.FC<Props> = ({ files, setFiles, loading, error, setError, fileLimit }) => {
  const prevFilesRef = useRef<File[]>([])
  const [fileError, setFileError] = useState<FileError | null>(null)
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFileError(null)
    const newFiles = acceptedFiles.filter(file => {
      return !prevFilesRef.current.some(
        (existing: File) => existing.name === file.name && existing.size === file.size
      )
    })
    if (newFiles.length + prevFilesRef.current.length > fileLimit) {
      return
    }
    setFiles(prevFiles => [...prevFiles, ...newFiles])
  }, [])
  const { getRootProps, getInputProps, isDragAccept, fileRejections } = useDropzone({
    onDrop, accept: { 'image/*': [], 'audio/*': [] }, maxFiles: fileLimit, disabled: files.length >= fileLimit || loading || error,
    validator: (file) => {
      if (prevFilesRef.current.some((existing: File) => existing.name === file.name && existing.size === file.size)) {
        return {
          code: 'file-already-exists',
          message: 'Arquivo já foi anexado anteriormente.'
        }
      }
      if (file.type.startsWith('image/') && file.size > 5 * 1024 * 1024) {
        return {
          code: 'file-too-large',
          message: 'O tamanho máximo é 5MB.'
        }
      }
      if (file.type.startsWith('audio/') && file.size > 10 * 1024 * 1024) {
        return {
          code: 'file-too-large',
          message: 'O tamanho máximo é 10MB.'
        }
      }
      return null
    }
  })

  useEffect(() => {
    if (fileRejections.length > 0) {
      const firstError = fileRejections.find(({ errors }) => errors.length > 0)?.errors[0];
      if (firstError) {
        setFileError(firstError)
        return
      }
    }
  }, [fileRejections])

  useEffect(() => {
    prevFilesRef.current = files
  }, [files])

  // TODO: Refactor render on error/loading/drag/files_length
  return (
    <>
      <Box {...getRootProps()} sx={{
        border: '1px dashed',
        borderColor: error ? '#FFCDD2' : loading ? '#E0E0E03B' : isDragAccept ? '#6FC374' : files.length < fileLimit ? '#212121' : 'grey.300',
        borderRadius: '4px',
        backgroundColor: error ? '#FFEBEE' : loading ? '#F8F8F8' : isDragAccept ? '#F1F8E9' : files.length < fileLimit ? 'background.default' : '#FAFAFA',
        display: 'flex',
        height: '70px',
        padding: '24px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        alignSelf: 'stretch',
        cursor: files.length < fileLimit && !loading && !error ? 'pointer' : 'not-allowed',
        ...(files.length < fileLimit && !loading && !error && {
          '&:hover': {
            borderColor: 'blue.300',
            backgroundColor: '#F3F8FF',
          }
        }),
      }}>
        <input {...getInputProps()} data-testid="dropzone-input" />
        {error ? (
          <Box sx={{ textAlign: 'end' }} data-testid="dropzone-error">
            <Typography variant='body2' color='error.main' fontWeight={700}>
              <ErrorOutline /> Não foi possível carregar
            </Typography>
            <Typography variant='caption' color='#EF9A9A'> Tente novamente mais tarde</Typography>
          </Box>
          ) : (
            loading ? (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}
                data-testid="dropzone-loading"
              >
                <CircularProgress size={16} color="inherit" />
                <Box>
                  <Typography variant='body2' color='grey.600' fontWeight={700}
                  >
                    Enviando arquivos...
                  </Typography>
                  <Typography variant='caption' color='grey.600'>Aguarde a conclusão</Typography>
                </Box>
              </Box>
            ) :
            (isDragAccept ? (
          <Box data-testid="dropzone-accept">
          <Typography variant='body2' color='grey.600' fontWeight={700}
            >
              Solte os arquivos aqui
              <Typography variant='caption' color='grey.600'> (máx. {fileLimit} arquivos)</Typography>
            </Typography>
          </Box>
        ) : files.length < fileLimit ? (
          <Box data-testid="dropzone-enabled">
            <Typography variant='body2' color='grey.600' fontWeight={700}
            >
              Arraste áudios e imagens ou clique para enviar
              <Typography variant='caption' color='grey.600'> (máx. {fileLimit} arquivos)</Typography>
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}
            data-testid="dropzone-disabled"
          >
            <Typography variant='body2' color='#999' fontWeight={700}
            >Limite de {fileLimit} arquivos atingido</Typography>
            <Typography variant='body2' color='#CCC'
            >Remova arquivos para adicionar novos</Typography>
          </Box>
        )))}
      </Box>
      {files.length > 0 && (
        <Box
          data-testid="file-chips"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
        {files.map((file, index) => (
          <Chip size='small' sx={{
            padding: '3px 4px',
            justifyContent: 'space-between',
            width: 'calc(50% - 12px)',
          }}
          disabled={loading}
          key={index} label={file.name} onDelete={() => {
            setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
            setError(false)
          }} />
        ))}
        </Box>
      )}
      <Toast error={fileError} onClose={() => setFileError(null)} />
    </>
  )
}

export default AttachmentsDropzone
